import { useMemo } from "react";
import { Order } from "../interfaces/Order";

export type Period = "day" | "week" | "month" | "year" | "custom";

/* =========================
   DATE HELPERS
========================= */
/* =========================
   DATE HELPERS
========================= */
const toDate = (order: any): Date => {
  if (!order) return new Date();

  // Lista extensiva de campos posibles. Priorizamos dateSuccess si existe, luego date.
  const dateValue =
    order.dateSuccess ||
    order.date ||
    order.createdAt ||
    order.timestamp ||
    order.fecha ||
    order.time;

  if (!dateValue) return new Date();

  // Intento de conversión a Date
  let dateObj: Date;
  if (typeof dateValue.toDate === "function") {
    dateObj = dateValue.toDate();
  } else if (dateValue instanceof Date) {
    dateObj = dateValue;
  } else if (typeof dateValue === "number") {
    dateObj = new Date(dateValue);
  } else if (dateValue && typeof dateValue === "object" && (dateValue.seconds !== undefined || dateValue._seconds !== undefined)) {
    const s = dateValue.seconds !== undefined ? dateValue.seconds : dateValue._seconds;
    dateObj = new Date(s * 1000);
  } else {
    dateObj = new Date(dateValue);
  }

  return isNaN(dateObj.getTime()) ? new Date() : dateObj;
};

const getStartOfPeriod = (period: Period, customDate?: Date): Date => {
  const start = customDate ? new Date(customDate.getTime()) : new Date();
  start.setHours(0, 0, 0, 0);

  switch (period) {
    case "day":
    case "custom":
      break;
    case "week": {
      const day = start.getDay();
      const diff = start.getDate() - day + (day === 0 ? -6 : 1);
      start.setDate(diff);
      break;
    }
    case "month":
      start.setDate(1);
      break;
    case "year":
      start.setMonth(0, 1);
      break;
  }
  return start;
};

const getEndOfPeriod = (period: Period, customDate?: Date): Date => {
  const end = customDate ? new Date(customDate.getTime()) : new Date();
  
  switch (period) {
    case "day":
    case "custom":
      end.setHours(23, 59, 59, 999);
      break;
    case "week": {
      const day = end.getDay();
      const diff = end.getDate() - day + (day === 0 ? 0 : 7);
      end.setDate(diff);
      end.setHours(23, 59, 59, 999);
      break;
    }
    case "month":
      end.setMonth(end.getMonth() + 1, 0);
      end.setHours(23, 59, 59, 999);
      break;
    case "year":
      end.setMonth(11, 31);
      end.setHours(23, 59, 59, 999);
      break;
  }
  return end;
};

const getPreviousPeriodRange = (period: Period, customDate?: Date): { start: Date; end: Date } => {
  const startCurrent = getStartOfPeriod(period, customDate);
  const end = new Date(startCurrent);
  end.setMilliseconds(-1);

  const start = new Date(startCurrent);
  switch (period) {
    case "day":
    case "custom":
      start.setDate(start.getDate() - 1);
      break;
    case "week":
      start.setDate(start.getDate() - 7);
      break;
    case "month":
      start.setMonth(start.getMonth() - 1);
      break;
    case "year":
      start.setFullYear(start.getFullYear() - 1);
      break;
  }
  return { start, end };
};

/* =========================
   ANALYTICS HOOK
========================= */
export const useAnalytics = (orders: Order[], period: Period, customDate?: Date, transactions: any[] = []) => {
  return useMemo(() => {
    const startOfPeriod = getStartOfPeriod(period, customDate);
    const endOfPeriod = getEndOfPeriod(period, customDate);
    const { start: startPrev, end: endPrev } = getPreviousPeriodRange(period, customDate);

    /* 🔹 Filtrar por períodos */
    const filteredOrders = orders.filter((order) => {
      const date = toDate(order);
      return date >= startOfPeriod && date <= endOfPeriod;
    });

    const previousOrders = orders.filter((order) => {
      const date = toDate(order);
      return date >= startPrev && date <= endPrev;
    });

    const filteredTransactions = transactions.filter((t) => {
      const date = toDate(t);
      return date >= startOfPeriod && date <= endOfPeriod;
    });
    
    const totalExpenses = filteredTransactions
      .filter((t) => t.type === "expense" || t.type === "egreso")
      .reduce((acc, t) => acc + (Number(t.amount) || 0), 0);

    /* 🔹 Métodos de Pago y Tipos de Orden */
    const paymentMethodsMap: Record<string, number> = {};
    const orderTypesMap: Record<string, number> = {};
    const paymentRevenue = { efectivo: 0, tarjeta: 0, transferencia: 0, otros: 0 };
    let totalTips = 0;
    let totalUsdReceived = 0;
    let totalChangeInCOP = 0;
    let totalDeliverySales = 0;
    let totalCancelledValue = 0;
    let totalCancelledCount = 0;

    /* 🔹 Timings & Workers */
    let totalCompletedOrdersForTime = 0;
    let sumTotalDuration = 0;
    const stageSumMap: Record<string, number> = {};
    const stageCountMap: Record<string, number> = {};
    const workerMetricsMap: Record<string, { id: string, name: string, role: string, ordersCount: number, productsCount: number, totalMins: number, validTimeOrdersCount: number, history: any[], avgMins: number }> = {};

    /* 🔹 Revenue chart & Product analysis */
    const revenueMap: Record<string, number> = {};
    const productQtyMap: Record<string, number> = {};
    const productRevMap: Record<string, number> = {};
    let totalPromoProducts = 0;
    let totalPromoOrders = 0;
    let totalPromoRevenue = 0;

    let splitOrdersCount = 0;
    let splitOrdersTotal = 0;

    filteredOrders.forEach((order) => {
      const total = Number(order.total || (order as any).totalPrice || 0);
      
      if (order.state === "cancelada") {
        totalCancelledValue += total;
        totalCancelledCount += 1;
        return; // Excluir de los demás cálculos
      }

      const pm = order.paymentMethod?.toLowerCase() || "desconocido";
      const tip = Number(order.tipAmount || 0);
      totalTips += tip;
      totalUsdReceived += Number((order as any).usdReceived || 0);
      totalChangeInCOP += Number((order as any).changeInCOP || 0);

      if (order.orderType === "domicilio" || order.address || order.deliveryDriverId) {
        totalDeliverySales += total;
      }

      // No aplicar retención del 5% a tarjetas
      const netTotal = total;

      if (order.isSplitPayment && order.splitPayments && Array.isArray(order.splitPayments) && order.splitPayments.length > 0) {
        splitOrdersCount++;
        splitOrdersTotal += total;

        order.splitPayments.forEach((sp: any) => {
          const spMethod = (sp.method || "").toLowerCase();
          const spAmount = Number(sp.amount || 0);

          if (spMethod.includes("efectivo") || spMethod === "cash") {
            paymentRevenue.efectivo += spAmount;
          } else if (spMethod.includes("tarjeta") || spMethod.includes("card") || spMethod.includes("crédito") || spMethod.includes("debito") || spMethod.includes("débito") || spMethod.includes("credito")) {
            paymentRevenue.tarjeta += spAmount;
          } else if (spMethod.includes("transferencia") || spMethod.includes("nequi") || spMethod.includes("bancolombia") || spMethod.includes("daviplata") || spMethod.includes("pse")) {
            paymentRevenue.transferencia += spAmount;
          } else {
            paymentRevenue.otros += spAmount;
          }
          paymentMethodsMap[spMethod || "separado"] = (paymentMethodsMap[spMethod || "separado"] || 0) + 1;
        });
      } else {
        if (pm.includes("efectivo") || pm === "cash") {
          paymentRevenue.efectivo += total;
        } else if (pm.includes("tarjeta") || pm.includes("card") || pm.includes("crédito") || pm.includes("debito") || pm.includes("débito") || pm.includes("credito")) {
          paymentRevenue.tarjeta += netTotal;
        } else if (pm.includes("transferencia") || pm.includes("nequi") || pm.includes("bancolombia") || pm.includes("daviplata") || pm.includes("pse")) {
          paymentRevenue.transferencia += total;
        } else {
          paymentRevenue.otros += total;
        }
        paymentMethodsMap[pm] = (paymentMethodsMap[pm] || 0) + 1;
      }

      // Gráfico de ingresos (usando neto)
      const date = toDate(order);
      const dateString = date.toLocaleDateString();
      revenueMap[dateString] = (revenueMap[dateString] || 0) + netTotal;

      // Top productos
      let hasPromoInOrder = false;
      const orderProducts = order.products || (order as any).pizzas || [];
      orderProducts.forEach((p: any) => {
        const qty = Number(p.quantity || 1);
        const price = Number(p.price || 0);
        const pName = p.productName || p.name || 'Producto';
        productQtyMap[pName] = (productQtyMap[pName] || 0) + qty;
        // Sin retención
        const productNetPrice = price;
        productRevMap[pName] = (productRevMap[pName] || 0) + (qty * productNetPrice);
        if (p.appliedPromotion) {
          totalPromoProducts += qty;
          hasPromoInOrder = true;
        }
      });
      if (hasPromoInOrder) {
        totalPromoOrders++;
        totalPromoRevenue += netTotal;
      }

      const ot = order.orderType || "no especificado";
      orderTypesMap[ot] = (orderTypesMap[ot] || 0) + 1;

      // Timings (Only for completed or ready orders)
      if (["completada", "listo / caja"].includes(order.state)) {
        // Calculate Total Duration
        const getRawDate = (val: any) => {
          if (!val) return null;
          if (typeof val.toDate === "function") return val.toDate();
          if (val instanceof Date) return val;
          if (typeof val === "number") return new Date(val);
          if (val && typeof val === "object" && (val.seconds !== undefined || val._seconds !== undefined)) {
            return new Date((val.seconds ?? val._seconds) * 1000);
          }
          return new Date(val);
        };
        const sDate = getRawDate(order.date || (order as any).createdAt);
        const eDate = getRawDate(order.dateSuccess) || getRawDate(order.stateUpdatedAt);
        
        if (sDate && eDate) {
          const diffMins = (eDate.getTime() - sDate.getTime()) / 60000;
          if (diffMins > 0 && diffMins < 1440) { // Validations (less than 24h)
            sumTotalDuration += diffMins;
            totalCompletedOrdersForTime++;
          }
        }

        // Calculate Stage Durations
        if (order.stageDurations) {
          Object.entries(order.stageDurations).forEach(([stage, mins]) => {
            if (mins > 0 && mins < 1440) {
              stageSumMap[stage] = (stageSumMap[stage] || 0) + mins;
              stageCountMap[stage] = (stageCountMap[stage] || 0) + 1;
            }
          });
        }
      }

      // 🔹 Worker Metrics (Preparadores/Cocineros)
      const prepId = order.preparadorId || order.cookId;
      const prepName = order.preparadorName || order.cookName || 'Preparador';
      let orderProductsCount = 0;
      if (order.products && Array.isArray(order.products)) {
        orderProductsCount = order.products.reduce((acc, p) => acc + (Number(p.quantity) || 1), 0);
      }

      const orderDate = toDate(order);
      const orderProductsStr = order.products && Array.isArray(order.products) 
        ? order.products.map(p => `${p.quantity || 1}x ${p.productName}`).join(', ') 
        : 'Desconocido';
      const orderNum = (order as any).orderNumber || order.id?.slice(0, 5) || '00000';

      if (prepId && (order.state === 'listo / caja' || order.state === 'completada' || order.state === 'enEmpaque' || order.state === 'enCamino' || order.state === 'esperando_domiciliario' || order.state === 'entregado' || order.state === 'porCobrar')) {
        if (!workerMetricsMap[prepId]) {
          workerMetricsMap[prepId] = { id: prepId, name: prepName, role: 'Preparación', ordersCount: 0, productsCount: 0, totalMins: 0, validTimeOrdersCount: 0, history: [], avgMins: 0 };
        }
        const prepMins = (order.stageDurations?.["enPreparacion"] || 0);
        workerMetricsMap[prepId].ordersCount++;
        workerMetricsMap[prepId].productsCount += orderProductsCount;
        
        if (prepMins > 0 && prepMins < 1440) {
          workerMetricsMap[prepId].totalMins += prepMins;
          workerMetricsMap[prepId].validTimeOrdersCount++;
        }
        
        workerMetricsMap[prepId].history.push({
          orderId: order.id,
          orderNumber: orderNum,
          products: orderProductsStr,
          durationMins: prepMins,
          date: orderDate
        });
      }

      // 🔹 Worker Metrics (Domiciliarios)
      if (order.deliveryDriverId && (order.state === 'entregado' || order.state === 'completada')) {
        if (!workerMetricsMap[order.deliveryDriverId]) {
          workerMetricsMap[order.deliveryDriverId] = { id: order.deliveryDriverId, name: order.deliveryDriverName || 'Domiciliario', role: 'Domiciliario', ordersCount: 0, productsCount: 0, totalMins: 0, validTimeOrdersCount: 0, history: [], avgMins: 0 };
        }
        const deliveryMins = order.stageDurations?.["enCamino"] || 0;
        workerMetricsMap[order.deliveryDriverId].ordersCount++;
        workerMetricsMap[order.deliveryDriverId].productsCount += orderProductsCount;
        
        if (deliveryMins > 0 && deliveryMins < 1440) {
          workerMetricsMap[order.deliveryDriverId].totalMins += deliveryMins;
          workerMetricsMap[order.deliveryDriverId].validTimeOrdersCount++;
        }
        
        workerMetricsMap[order.deliveryDriverId].history.push({
          orderId: order.id,
          orderNumber: orderNum,
          products: orderProductsStr,
          durationMins: deliveryMins,
          date: orderDate
        });
      }
    });

    /* 🔹 KPIs actuales con descuento aplicado */
    const revenue = paymentRevenue.efectivo + paymentRevenue.tarjeta + paymentRevenue.transferencia + paymentRevenue.otros;
    const activeOrders = filteredOrders.filter(o => o.state !== "cancelada");
    const ordersCount = activeOrders.length;
    const users = new Set(activeOrders.map((o) => o.name)).size;
    const ticketAvg = ordersCount ? revenue / ordersCount : 0;

    /* 🔹 Growth (basado en neto) */
    const prevRevenue = previousOrders.reduce((sum, order) => {
      if (order.state === "cancelada") return sum;
      const pm = order.paymentMethod?.toLowerCase() || "";
      const total = Number(order.total || (order as any).totalPrice || 0);
      return sum + total;
    }, 0);
    const growth = prevRevenue ? ((revenue - prevRevenue) / prevRevenue) * 100 : (revenue && prevRevenue === 0) ? 100 : 0;

    /* 🔹 Estados */
    const delivered = filteredOrders.filter((o) => o.state === "completada").length;
    const cancelled = filteredOrders.filter((o) => o.state === "cancelada").length;

    /* 🔹 Revenue Chart formatting */
    const revenueChart = Object.entries(revenueMap)
      .map(([date, total]) => {
        const firstOrderOfDay = filteredOrders.find(o => toDate(o).toLocaleDateString() === date);
        const timestamp = firstOrderOfDay ? toDate(firstOrderOfDay).getTime() : 0;
        return { date, total, timestamp };
      })
      .sort((a, b) => a.timestamp - b.timestamp);

    /* 🔹 Top products formatting */
    const topProducts = Object.entries(productQtyMap)
      .map(([name, qty]) => ({ name, qty }))
      .sort((a, b) => b.qty - a.qty)
      .slice(0, 5);

    const topProductsByRevenue = Object.entries(productRevMap)
      .map(([name, netRevenue]) => ({ name, revenue: netRevenue }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);

    const paymentMethods = Object.entries(paymentMethodsMap).map(([name, value]) => ({ name, value }));
    const orderTypes = Object.entries(orderTypesMap).map(([name, value]) => ({ name, value }));

    /* 🔹 Plan Guía Analysis */
    const totalGuiaDiscount = filteredOrders.reduce((sum, order) => sum + Number(order.guiaDiscount || 0), 0);

    /* 🔹 Horas Pico (no se ven afectadas por el descuento) */
    const peakHoursMap: Record<string, number> = {};
    filteredOrders.forEach((order) => {
      const date = toDate(order);
      const hour = date.getHours();
      const formattedHour = `${hour.toString().padStart(2, "0")}:00`;
      peakHoursMap[formattedHour] = (peakHoursMap[formattedHour] || 0) + 1;
    });

    const peakHours = Object.entries(peakHoursMap)
      .map(([time, orders]) => ({ time, orders }))
      .sort((a, b) => a.time.localeCompare(b.time));

    const avgTotalTime = totalCompletedOrdersForTime > 0 ? Math.round(sumTotalDuration / totalCompletedOrdersForTime) : 0;
    const avgStageTimes = Object.entries(stageSumMap).map(([stage, totalMins]) => ({
      stage,
      avgMins: Math.round(totalMins / (stageCountMap[stage] || 1))
    })).sort((a, b) => b.avgMins - a.avgMins);

    const workerMetrics = Object.values(workerMetricsMap).map(worker => ({
      ...worker,
      avgMins: worker.validTimeOrdersCount > 0 ? Math.round(worker.totalMins / worker.validTimeOrdersCount) : 0,
      history: worker.history.sort((a, b) => b.date.getTime() - a.date.getTime())
    })).sort((a, b) => b.productsCount - a.productsCount);

    return {
      revenue,
      orders: ordersCount,
      users,
      ticketAvg,
      delivered,
      cancelled,
      revenueChart,
      topProducts,
      topProductsByRevenue,
      paymentMethods,
      orderTypes,
      peakHours,
      growth,
      paymentRevenue,
      totalTips,
      totalGuiaDiscount,
      totalUsdReceived,
      totalChangeInCOP,
      totalDeliverySales,
      totalCancelledValue,
      totalCancelledCount,
      totalPromoProducts,
      totalPromoOrders,
      totalPromoRevenue,
      totalExpenses,
      avgTotalTime,
      avgStageTimes,
      workerMetrics,
      splitOrdersCount,
      splitOrdersTotal
    };
  }, [orders, period, customDate]);
};
